import React from "react";
import styles from "./CarComparisonTable.module.scss";
import assetLoader from "@utils/internalImageLoder";
import Image from "@components/atoms/Image";
import Button from "@components/atoms/Button";
import CustomLink from "@components/atoms/Link";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";

interface CarComparisonTableProps {
  cars: any[];
  showCompareCTA?: boolean;
  handleEditCar?: (colIndex: number) => void;
  maxColumns: number;
  maxSpecs: number;
  imageWidth: number;
  imageHeight: number;
  showDesktopCompareCTA: boolean;
  showMobileCompareCTA: boolean;
  brandName?: string;
  modelName?: string;
  cityName?: string;
  isLoggedIn?: boolean;
  carAvailability?: string;
  pageType?: string;
  isMobile?: boolean;
  isInApp?: boolean;
}

const CarComparisonTable = ({
  cars,
  showCompareCTA = false,
  showDesktopCompareCTA = false,
  showMobileCompareCTA = false,
  handleEditCar,
  maxColumns,
  maxSpecs,
  imageWidth,
  imageHeight,
  brandName,
  modelName,
  cityName,
  carAvailability,
  pageType,
  isMobile,
  isInApp
}: CarComparisonTableProps) => {
  const comparisonSlug = showMobileCompareCTA ? cars[1]?.comparisonSlug : "";
  return (
    <div className={styles.tableWrapper}>
      <div className={styles.table}>
        {/* First row: car image and name (edit button for mobile only) */}
        <div className={styles["table-row"]}>
          {cars.slice(0, maxColumns).map((car, colIndex) => (
            <div className={styles["table-cell"]} key={colIndex}>
              <div className={styles.carImageCell}>
                <div className={styles.carImageContainer}>
                  <ImageResponsive
                    filePath={`${toKebabCase(car?.brand)}/${toKebabCase(
                      car?.model
                    )}`}
                    imageTag={`Hero-Transparent`}
                    alt={car?.model}
                    width={imageWidth}
                    height={imageHeight}
                    className={styles.carImage}
                  />
                </div>

                <div className={styles.headingWrapper}>
                  <div className={styles.carNameCell}>{car.name}</div>
                  {isMobile && (
                    <Button
                      onButtonClick={() =>
                        handleEditCar && handleEditCar(colIndex)
                      }
                      className={
                        colIndex === 1
                          ? styles.editBtn + " " + styles.mobileOnly
                          : styles.editBtn + " " + styles.hide
                      }
                      segmentEventProps={{
                        eventName: "action",
                        pageType: pageType,
                        eventProperties: {
                          action_type: "tap_btn",
                          action_name: "tap_btn_choose_a_model",
                          car_availability: carAvailability,
                          city_name: cityName,
                          vehicle_make: brandName,
                          vehicle_model: modelName,
                        },
                      }}
                    >
                      <Image
                        src={`${assetLoader()}/icons/Edit_Pencil.svg`}
                        alt="Edit"
                        width={20}
                        height={20}
                        loading="lazy"
                        className={styles.carNameCellEditIcon}
                      />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {Array.from({ length: maxSpecs }, (_, rowIndex) => (
          <div className={styles["table-row"]} key={rowIndex + 1}>
            {cars.slice(0, maxColumns).map((car, colIndex) => (
              <div className={styles["table-cell"]} key={colIndex}>
                <div className={styles.spec}>
                  {car.specs?.[rowIndex] ? (
                    <>
                      <div className={styles.specLabel}>
                        {`${car.specs[rowIndex].label}`}
                      </div>
                      <div className={styles.specValue}>
                        {`${car.specs[rowIndex].value} ${car.specs[rowIndex].unit}`}
                      </div>
                    </>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        {showCompareCTA && showDesktopCompareCTA && (
          <div className={`${styles["table-row"]} ${styles.ctaRow}`}>
            {cars.slice(0, maxColumns).map((car, colIndex) => (
              <div className={styles["table-cell"]} key={colIndex}>
                {car.actionText && (
                  <CustomLink
                    size="ds"
                    className={styles.moreDetailsBtn}
                    href={`/compare/${car.comparisonSlug}`}
                    segmentEventProps={{
                      eventName: "action",
                      pageType: pageType,
                      eventProperties: {
                        action_type: "tap_btn",
                        action_name: "tap_btn_detailed_comparison",
                        car_availability: carAvailability,
                        city_name: cityName,
                        vehicle_make: brandName,
                        vehicle_model: modelName,
                      },
                    }}
                  >
                    {car.actionText}
                    <Image
                      src={`${assetLoader()}/icons/Chevron_right_blue.svg`}
                      alt="Chevron right"
                      width={20}
                      height={20}
                      loading="lazy"
                    />
                  </CustomLink>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {!isInApp && showCompareCTA && showMobileCompareCTA && (
        <div className={styles.detailedComparisonRow}>
          <CustomLink
            size="ds"
            className={styles.moreDetailsBtn}
            href={`/compare/${comparisonSlug}`}
            segmentEventProps={{
              eventName: "action",
              pageType: pageType,
              eventProperties: {
                action_type: "tap_btn",
                action_name: "tap_btn_detailed_comparison",
                car_availability: carAvailability,
                city_name: cityName,
                vehicle_make: brandName,
                vehicle_model: modelName,
              },
            }}
          >
            See detailed comparison
            <Image
              src={`${assetLoader()}/icons/Chevron_right_blue.svg`}
              alt="Chevron right"
              width={20}
              height={20}
              loading="lazy"
            />
          </CustomLink>
        </div>
      )}
    </div>
  );
};

export default CarComparisonTable;
